import { useEffect, useRef, useState } from 'react';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';

export type AutosaveState = 'idle' | 'saving' | 'saved';

/**
 * Derives a presentational autosave status from the node editor's real
 * `isDirty` flag. This adds NO new persistence behavior — the node editor is
 * already pure-autosave (edits debounce-persist via the store's
 * `scheduleAutoSave`, and `isDirty` flips false once applied). We simply
 * surface that existing lifecycle:
 *
 *  - `isDirty` true            → `saving`  (edits pending the debounced apply)
 *  - `isDirty` false after true → `saved`  (just persisted; auto-fades to idle)
 *  - otherwise                 → `idle`
 */
export function useAutosaveStatus(): AutosaveState {
  const isDirty = useNodeEditorStore((s) => s.isDirty);
  const [state, setState] = useState<AutosaveState>('idle');
  const wasDirty = useRef(isDirty);

  useEffect(() => {
    if (isDirty) {
      setState('saving');
      wasDirty.current = true;
      return;
    }
    // Clean now — if we were mid-edit, briefly confirm the save then settle.
    if (wasDirty.current) {
      wasDirty.current = false;
      setState('saved');
      const timer = setTimeout(() => setState('idle'), 3000);
      return () => clearTimeout(timer);
    }
    return;
  }, [isDirty]);

  return state;
}
